import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from './cn';

export type DrawerSide = 'right' | 'left';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  side?: DrawerSide;
  size?: DrawerSize;
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  dismissibleOnBackdrop?: boolean;
  ariaLabel?: string;
  className?: string;
}

const SIZE_PX: Record<DrawerSize, string> = {
  sm: 'w-[360px] max-w-[calc(100vw-1rem)]',
  md: 'w-[504px] max-w-[calc(100vw-1rem)]',
  lg: 'w-[648px] max-w-[calc(100vw-1rem)]',
  xl: 'w-[864px] max-w-[calc(100vw-1rem)]',
};

export function Drawer({
  open,
  onClose,
  side = 'right',
  size = 'md',
  title,
  footer,
  children,
  dismissibleOnBackdrop = true,
  ariaLabel,
  className,
}: DrawerProps) {
  const reduced = useReducedMotion();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      (first ?? panelRef.current)?.focus();
    }, 0);
    return () => {
      window.clearTimeout(t);
      previousFocusRef.current?.focus?.();
    };
  }, [open]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );

  const xInitial = reduced ? 0 : side === 'right' ? '100%' : '-100%';

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-[40] pointer-events-none"
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : ariaLabel}
          onKeyDown={onKeyDown}
        >
          {dismissibleOnBackdrop && (
            <div className="absolute inset-0 bg-black/40 pointer-events-auto" onClick={onClose} aria-hidden />
          )}

          <motion.div
            key="panel"
            ref={panelRef}
            tabIndex={-1}
            initial={{ x: xInitial }}
            animate={{ x: 0 }}
            exit={{ x: xInitial }}
            transition={{ duration: reduced ? 0 : 0.28, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'fixed inset-y-0 flex flex-col pointer-events-auto bg-surface-0 border-border-subtle',
              side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
              SIZE_PX[size],
              className,
            )}
          >
            {title !== undefined && (
              <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border-subtle px-4">
                <div className="min-w-0 flex-1 truncate text-sm font-semibold text-text-primary">{title}</div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                >
                  <X className="h-4 w-4" />
                </button>
              </header>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>

            {footer && (
              <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle bg-surface-base px-4 py-3">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
